import { useState } from 'react';
import { useIntl } from 'react-intl';
import { Navigation as NavigationModule } from 'swiper';
import { SwiperSlide } from 'swiper/react';

import * as GS from '@/components/Cms/Grid/Grid.styles';
import { Visibility } from '@/components/Visibility';
import type { ContentfulPlaceholderServiceFragment } from '@/graphql/contentful/api';
import { useIsSmDown } from '@/hooks/useMedia';
import { useToggle } from '@/hooks/useToggle';
import { aboutMessages } from '@/messages/about';
import { getBreakpointSize } from '@/theme';

import { Caliber } from '../Cms/Card/components/Caliber/Caliber';
import { CaliberGroup } from './const';

import * as S from './PlaceholderCaliberTab.styles';
export interface AccordionProps {
  handleChange?: () => void;
  isOpen?: boolean;
  label: string;
  openType: boolean;
  setCaliberType?: (label: string) => void;
}
const caliberMessages = {
  chronographs: aboutMessages.chronographs,
  threeHands: aboutMessages.threeHands,
  superquartz: aboutMessages.superquartz,
  nonManufacture: aboutMessages.nonManufacture,
};
export const AccordionItem = (props: AccordionProps) => {
  const { formatMessage } = useIntl();

  const message = caliberMessages[props.label as keyof typeof caliberMessages];
  const handleChange = (label: string) => {
    props?.handleChange?.();
    props?.setCaliberType?.(label);
  };
  return (
    <S.CaliberAccordionItem
      $isOpen={props?.openType}
      className='AccordionItemStyle'
      data-testid='AccordionItem'
      onClick={() => handleChange(props.label)}
    >
      {formatMessage(message)}
    </S.CaliberAccordionItem>
  );
};
export const PlaceholderCaliberTab = (
  _props: ContentfulPlaceholderServiceFragment,
) => {
  const withTab = _props.type === 'CaliberTab';
  const { formatMessage } = useIntl();
  const [caliberType, setCaliberType] = useState(
    withTab ? 'chronographs' : 'nonManufacture',
  );

  const isMobileView = useIsSmDown();
  const caliberList = ['chronographs', 'threeHands', 'superquartz'];
  const slidesPerView = Number(4);
  const breakpoints = {
    [getBreakpointSize('xs')]: {
      slidesPerView: 1.5,
      spaceBetween: 0,
    },
    [getBreakpointSize('md')]: {
      slidesPerView: 4,
      spaceBetween: 0,
    },
    [getBreakpointSize('lg')]: {
      slidesPerView: 4,
      spaceBetween: 0,
    },
  };
  const caliberDescriptions = {
    chronographs: aboutMessages.descriptionChronographs,
    threeHands: aboutMessages.descriptionThreeHands,
    superquartz: aboutMessages.descriptionSuperquartz,
    nonManufacture: aboutMessages.descriptionNonManufacture,
  };
  const renderCaliberItem = () => {
    const messageKey =
      caliberDescriptions[caliberType as keyof typeof caliberDescriptions] ||
      aboutMessages.descriptionNonManufacture;
    const isDefault = caliberType === 'nonManufacture';

    return (
      <S.CaliberItem $default={isDefault}>
        {formatMessage(messageKey)}
      </S.CaliberItem>
    );
  };
  const renderCaliberTab = (type: string) => {
    const message =
      caliberMessages[type as keyof typeof caliberDescriptions] ||
      caliberMessages.nonManufacture;

    return <S.CaliberTab>{formatMessage(message)}</S.CaliberTab>;
  };

  const [isOpen, toggle] = useToggle();

  return (
    <S.Wrapper>
      <Visibility smVisible>
        {withTab ? (
          <S.CaliberTabs>
            {caliberList.map((item) => {
              return (
                <S.CaliberTabBox
                  key={item}
                  $active={item === caliberType}
                  onClick={() => setCaliberType(item)}
                >
                  {renderCaliberTab(item)}
                </S.CaliberTabBox>
              );
            })}
          </S.CaliberTabs>
        ) : (
          <S.CaliberTitle>
            {formatMessage(aboutMessages.nonManufacture)}
          </S.CaliberTitle>
        )}
      </Visibility>

      <Visibility smHidden>
        {withTab ? (
          <S.CaliberAccordionWrapper
            isOpen={isOpen}
            onClick={toggle}
            title={
              <AccordionItem
                key='AccordionItem'
                isOpen={isOpen}
                label={caliberType}
                openType={false}
              ></AccordionItem>
            }
          >
            {caliberList
              ?.filter((item) => item !== caliberType)
              .map((item) => {
                return (
                  <AccordionItem
                    key={item}
                    handleChange={toggle}
                    label={item}
                    openType={true}
                    setCaliberType={setCaliberType}
                  ></AccordionItem>
                );
              })}
          </S.CaliberAccordionWrapper>
        ) : (
          <S.CaliberTitle>
            {formatMessage(aboutMessages.nonManufacture)}
          </S.CaliberTitle>
        )}
      </Visibility>

      <S.CaliberContainer $withTab={withTab}>
        {renderCaliberItem()}
        <S.SwiperContainer>
          <GS.StyledSwiper
            $hideNavigation={slidesPerView >= CaliberGroup[caliberType].length}
            breakpoints={breakpoints}
            centerInsufficientSlides
            centeredSlidesBounds
            modules={[NavigationModule]}
            navigation={!isMobileView}
            separator='false'
            slidesPerView={1.5}
            watchSlidesProgress
          >
            {CaliberGroup[caliberType].map((item) => {
              return (
                <SwiperSlide key={item.id}>
                  <Caliber {...item}></Caliber>
                </SwiperSlide>
              );
            })}
          </GS.StyledSwiper>
        </S.SwiperContainer>
      </S.CaliberContainer>
    </S.Wrapper>
  );
};
